// src/components/trading/AccountDetails.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { TradingAccount } from '../../types/trading';
import AccountHeader from './AccountHeader';
import AccountOverview from './AccountOverview';
import LoadingState from '../LoadingState';

const AccountDetails: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [account, setAccount] = useState<TradingAccount | null>(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Fetch the account data
  useEffect(() => {
    const fetchAccountData = async () => {
      if (!currentUser || !accountId) {
        navigate('/dashboard');
        return;
      }
      
      try {
        setIsLoadingAccount(true);
        setError(null);
        
        // Get the account document from Firestore
        const accountDocRef = doc(db, 'users', currentUser.uid, 'tradingAccounts', accountId);
        const accountSnapshot = await getDoc(accountDocRef);
        
        if (!accountSnapshot.exists()) {
          setError('Trading account not found');
          navigate('/dashboard');
          return;
        }
        
        // Set the account data
        setAccount({
          id: accountSnapshot.id,
          ...accountSnapshot.data()
        } as TradingAccount);
        
      } catch (err) {
        console.error('Error fetching account:', err);
        setError('Failed to load trading account details');
      } finally {
        setIsLoadingAccount(false);
      }
    };
    
    fetchAccountData();
  }, [currentUser, accountId, navigate]);

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  if (isLoadingAccount) {
    return <LoadingState message="Loading account details..." />;
  }

  if (error) {
    return (
      <div className="px-4 py-5 sm:p-6 text-center">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return null;
  }

  // Define tabs for the header
  const tabs = [
    { id: 'overview', label: 'Overview' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
        <AccountHeader 
          account={account}
          activeTab={activeTab}
        setActiveTab={setActiveTab}
          tabs={tabs}
        />
      
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`${
                activeTab === 'overview'
                  ? 'border-t1blue text-t1blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
          </nav>
              </div>
              
        {/* Tab Content */}
        <div className="mt-6">
        {activeTab === 'overview' && (
          <AccountOverview 
            account={account}
              isLoading={isLoadingAccount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;